import React from 'react';
import { DataFrame } from './dataUtils';
import { UMAP } from 'umap-js';

interface UmapChartProps {
  df: DataFrame;
  genes: string[];
  geneGroupCounts: Record<string, number>;
}

function computeUMAP(df: DataFrame, genes: string[], nComponents = 2) {
  const samples = df.columns;
  // Use only the provided genes
  const matrix = genes.map(gene => samples.map(s => df.data[gene][s] ?? 0));
  // UMAP expects array of arrays (genes x samples)
  function seededRandom(seed: number) {
    let x = Math.sin(seed) * 10000;
    return function() {
      x = Math.sin(x) * 10000;
      return x - Math.floor(x);
    };
  }
  const random = seededRandom(42); // fixed seed
  const umap = new UMAP({ nComponents, nNeighbors: Math.min(15, Math.floor(genes.length / 2)), minDist: 0.1, random });
  const embedding = umap.fit(matrix);
  return { genes, coords: embedding };
}

const UmapChart: React.FC<UmapChartProps> = ({ df, genes, geneGroupCounts }) => {
  const [umap, setUmap] = React.useState<{ genes: string[]; coords: number[][] } | null>(null);
  const [tooltip, setTooltip] = React.useState<{
    x: number;
    y: number;
    gene: string;
    expr: Record<string, number> | null;
    visible: boolean;
  }>({ x: 0, y: 0, gene: '', expr: null, visible: false });
  const [search, setSearch] = React.useState('');
  const [highlightedGene, setHighlightedGene] = React.useState<string | null>(null);
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [showGeneLabels, setShowGeneLabels] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);

  // --- Zoom state and helpers (must be after all variable declarations that are used) ---
  // (width, height, dataMinX, dataMaxX, dataMinY, dataMaxY, centerX, centerY, pad must be defined before this block)
// Chart dimensions and center (must be defined before zoom helpers)
const width = 1200;
const height = 840;
const centerX = width / 2;
const centerY = height / 2;
const pad = 0.08;

// If umap is not loaded yet, use defaults for min/max
let dataMinX = 0, dataMaxX = 1, dataMinY = 0, dataMaxY = 1;
if (umap) {
    const xs = umap.coords.map(c => c[0]);
    const ys = umap.coords.map(c => c[1]);
    dataMinX = Math.min(...xs);
    dataMaxX = Math.max(...xs);
    dataMinY = Math.min(...ys);
    dataMaxY = Math.max(...ys);
}
  // Zoom state
  const [zoom, setZoom] = React.useState<null | { x0: number; y0: number; x1: number; y1: number }>(null);
  const [dragRect, setDragRect] = React.useState<null | { x0: number; y0: number; x1: number; y1: number }>(null);

  // Helper to convert SVG mouse event to data coordinates
  function svgPointToData(evt: React.MouseEvent) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const svgX = evt.clientX - rect.left;
    const svgY = evt.clientY - rect.top;
    let x, y;
    if (zoom) {
      const zx0 = Math.min(zoom.x0, zoom.x1), zx1 = Math.max(zoom.x0, zoom.x1);
      const zy0 = Math.min(zoom.y0, zoom.y1), zy1 = Math.max(zoom.y0, zoom.y1);
      x = zx0 + (svgX / width) * (zx1 - zx0);
      y = zy1 - (svgY / height) * (zy1 - zy0);
    } else {
      x = dataMinX + (svgX / width) * (dataMaxX - dataMinX);
      y = dataMaxY - (svgY / height) * (dataMaxY - dataMinY);
    }
    return { x, y };
  }

  // Mouse event handlers for drag zoom
  const handleSvgMouseDown = (evt: React.MouseEvent) => {
    if (zoom) {
      setZoom(null); // Reset zoom on click when zoomed
      setDragRect(null);
      return;
    }
    const { x, y } = svgPointToData(evt);
    setDragRect({ x0: x, y0: y, x1: x, y1: y });
    window.addEventListener('mousemove', handleSvgMouseMove as any);
    window.addEventListener('mouseup', handleSvgMouseUp as any);
  };
  const handleSvgMouseMove = (evt: MouseEvent) => {
    setDragRect(rect => {
      if (!rect) return null;
      const svg = svgRef.current;
      if (!svg) return rect;
      const svgRect = svg.getBoundingClientRect();
      const svgX = evt.clientX - svgRect.left;
      const svgY = evt.clientY - svgRect.top;
      let x, y;
      if (zoom) {
        const zx0 = Math.min(zoom.x0, zoom.x1), zx1 = Math.max(zoom.x0, zoom.x1);
        const zy0 = Math.min(zoom.y0, zoom.y1), zy1 = Math.max(zoom.y0, zoom.y1);
        x = zx0 + (svgX / width) * (zx1 - zx0);
        y = zy1 - (svgY / height) * (zy1 - zy0);
      } else {
        x = dataMinX + (svgX / width) * (dataMaxX - dataMinX);
        y = dataMaxY - (svgY / height) * (dataMaxY - dataMinY);
      }
      return { ...rect, x1: x, y1: y };
    });
  };
  const handleSvgMouseUp = (evt: MouseEvent) => {
    setDragRect(rect => {
      if (!rect) return null;
      if (Math.abs(rect.x1 - rect.x0) > 1e-6 && Math.abs(rect.y1 - rect.y0) > 1e-6) {
        setZoom({ x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1 });
      }
      return null;
    });
    window.removeEventListener('mousemove', handleSvgMouseMove as any);
    window.removeEventListener('mouseup', handleSvgMouseUp as any);
  };

  // Adjust scaleX/scaleY for zoom
  const [zoomedMinX, zoomedMaxX, zoomedMinY, zoomedMaxY] = zoom
    ? [Math.min(zoom.x0, zoom.x1), Math.max(zoom.x0, zoom.x1), Math.min(zoom.y0, zoom.y1), Math.max(zoom.y0, zoom.y1)]
    : [dataMinX, dataMaxX, dataMinY, dataMaxY];
  const zoomedRangeX = zoomedMaxX - zoomedMinX;
  const zoomedRangeY = zoomedMaxY - zoomedMinY;
  const scaleXZoom = (x: number) => centerX + ((x - (zoomedMinX + zoomedMaxX) / 2) / (zoomedRangeX / 2 + 1e-9)) * (centerX * (1 - pad));
  const scaleYZoom = (y: number) => centerY - ((y - (zoomedMinY + zoomedMaxY) / 2) / (zoomedRangeY / 2 + 1e-9)) * (centerY * (1 - pad));

  // Add back the handleBlur function
  const handleBlur = () => setTimeout(() => setShowSuggestions(false), 120);

  // Fix: ensure umap is loaded before using umapGenes, coords, xs, ys, dataMinX, etc.
  React.useEffect(() => {
    if (df && genes && genes.length > 0) {
      setUmap(computeUMAP(df, genes, 2));
    }
  }, [df, genes]);

  // Update suggestions when search changes
  React.useEffect(() => {
    if (!umap) {
      setSuggestions([]);
      return;
    }
    const val = search.trim().toLowerCase();
    if (!val) {
      setSuggestions([]);
      return;
    }
    const filtered = umap.genes.filter(g => g.toLowerCase().includes(val));
    setSuggestions(filtered.slice(0, 20));
  }, [search, umap]);

  // Highlight gene when search matches exactly
  React.useEffect(() => {
    if (!umap) return;
    const val = search.trim();
    if (val && umap.genes.includes(val)) {
      setHighlightedGene(val);
    } else {
      setHighlightedGene(null);
    }
  }, [search, umap]);

  if (!umap) {
    // Show a loading spinner or message instead of returning null
    return (
      <div style={{ width: '100%', textAlign: 'center', marginTop: 80, fontSize: 22, color: '#888' }}>
        Loading UMAP projection...
      </div>
    );
  }

  const { genes: umapGenes, coords } = umap;
  const xs = coords.map(c => c[0]);
  const ys = coords.map(c => c[1]);
  // Use samples from df.columns after umap is loaded
  const samples = df.columns;

  // For color scale: get min/max group count for these genes
  const groupCounts = umapGenes.map((g: string) => geneGroupCounts[g] || 0);
  const minCount = Math.min(...groupCounts);
  const maxCount = Math.max(...groupCounts);
  // Color scale: interpolate from #b3d1ff (light blue) to #003366 (dark blue)
  function colorByCount(count: number) {
    if (maxCount === minCount) return '#2a2a4a';
    const t = (count - minCount) / (maxCount - minCount);
    // Interpolate between two colors
    const r = Math.round(179 + (0 - 179) * t);
    const g = Math.round(209 + (51 - 209) * t);
    const b = Math.round(255 + (102 - 255) * t);
    return `rgb(${r},${g},${b})`;
  }

  return (
    <div style={{ margin: '48px auto 0 auto', maxWidth: 1300, background: '#f9f9f9', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: 36, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Title and search input in a row */}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 28, justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 28, color: '#2a2a4a', fontWeight: 800, textAlign: 'left', margin: 0, letterSpacing: 0.5, textShadow: '0 2px 8px #fff' }}>
          Gene Expression Similarity Plot
        </h2>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', position: 'relative', width: 370, gap: 8 }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search gene name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: 240,
              fontSize: 18,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1.5px solid #b0b6c3',
              outline: 'none',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              fontWeight: 500,
              color: '#2a2a4a',
              background: '#fff',
              transition: 'border 0.2s',
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
                setShowSuggestions(false);
              }
              if (e.key === 'ArrowDown' && suggestions.length > 0) {
                const el = document.getElementById('gene-suggestion-0');
                if (el) (el as HTMLDivElement).focus();
              }
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={handleBlur}
            autoComplete="off"
          />
          {/* Toggle gene labels button, now smaller and to the right, with eye icon */}
          <button
            style={{
              padding: '6px 14px',
              borderRadius: 7,
              border: '1.5px solid #b0b6c3',
              background: showGeneLabels ? '#e6f0ff' : '#f7f8fa',
              color: '#2a2a4a',
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'background 0.2s, border 0.2s',
              outline: 'none',
              marginLeft: 6,
              whiteSpace: 'nowrap',
              minWidth: 44,
              height: 38,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => setShowGeneLabels(v => !v)}
            tabIndex={0}
            aria-pressed={showGeneLabels}
            title={showGeneLabels ? 'Hide gene names' : 'Show gene names'}
          >
            {showGeneLabels ? (
              // Open eye SVG
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="11" cy="11" rx="8.5" ry="5.5" stroke="#2a2a4a" strokeWidth="2" fill="#fff" />
                <circle cx="11" cy="11" r="2.5" fill="#2a2a4a" />
              </svg>
            ) : (
              // Closed eye SVG
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="11" cy="11" rx="8.5" ry="5.5" stroke="#2a2a4a" strokeWidth="2" fill="#fff" />
                <line x1="5" y1="17" x2="17" y2="5" stroke="#e94f37" strokeWidth="2.2" />
                <circle cx="11" cy="11" r="2.5" fill="#b0b6c3" />
              </svg>
            )}
          </button>
          {/* Suggestions dropdown remains absolutely positioned below input */}
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 44,
              left: 0,
              width: 240,
              background: '#fff',
              border: '1.5px solid #b0b6c3',
              borderRadius: 8,
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              zIndex: 10,
              maxHeight: 260,
              overflowY: 'auto',
            }}>
              {suggestions.map((g, idx) => (
                <div
                  id={`gene-suggestion-${idx}`}
                  key={g}
                  tabIndex={0}
                  style={{
                    padding: '10px 16px',
                    fontSize: 17,
                    color: g === highlightedGene ? '#e94f37' : '#2a2a4a',
                    background: g === highlightedGene ? 'rgba(233,79,55,0.08)' : 'none',
                    cursor: 'pointer',
                    fontWeight: g === highlightedGene ? 700 : 500,
                    outline: 'none',
                  }}
                  onMouseDown={e => {
                    e.preventDefault();
                    setSearch(g);
                    setHighlightedGene(g);
                    setShowSuggestions(false);
                    inputRef.current?.blur();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSearch(g);
                      setHighlightedGene(g);
                      setShowSuggestions(false);
                      inputRef.current?.blur();
                    } else if (e.key === 'ArrowDown' && idx < suggestions.length - 1) {
                      const el = document.getElementById(`gene-suggestion-${idx + 1}`);
                      if (el) (el as HTMLDivElement).focus();
                    } else if (e.key === 'ArrowUp') {
                      if (idx === 0) {
                        inputRef.current?.focus();
                      } else {
                        const el = document.getElementById(`gene-suggestion-${idx - 1}`);
                        if (el) (el as HTMLDivElement).focus();
                      }
                    }
                  }}
                >
                  {g}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{
        borderRadius: 14,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 18,
        width: '100%', // changed from width: width
        minWidth: width, // ensure at least SVG width
        overflowX: 'auto',
        background: 'none',
        boxSizing: 'border-box', // ensure padding/border included
      }}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ display: 'block', margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
          onMouseDown={handleSvgMouseDown}
        >
          {/* Centered Axes */}
          <line x1={0} y1={centerY} x2={width} y2={centerY} stroke="#bbb" strokeWidth={1.2} />
          <line x1={centerX} y1={0} x2={centerX} y2={height} stroke="#bbb" strokeWidth={1.2} />
          {/* Drag rectangle for zoom */}
          {dragRect && (
            <rect
              x={Math.min(scaleXZoom(dragRect.x0), scaleXZoom(dragRect.x1))}
              y={Math.min(scaleYZoom(dragRect.y0), scaleYZoom(dragRect.y1))}
              width={Math.abs(scaleXZoom(dragRect.x1) - scaleXZoom(dragRect.x0))}
              height={Math.abs(scaleYZoom(dragRect.y1) - scaleYZoom(dragRect.y0))}
              fill="rgba(100,150,255,0.18)"
              stroke="#2a2a4a"
              strokeDasharray="4 2"
              strokeWidth={2}
              pointerEvents="none"
            />
          )}
          {/* Points: non-highlighted first, then highlighted on top */}
          {coords.map(([x, y], i) => {
            const gene = umapGenes[i];
            const isHighlighted = highlightedGene && gene === highlightedGene;
            if (!isHighlighted) {
              const count = geneGroupCounts[gene] || 0;
              return (
                <g key={gene}>
                  <circle
                    cx={scaleXZoom(x)}
                    cy={scaleYZoom(y)}
                    r={7}
                    fill={colorByCount(count)}
                    stroke="none"
                    opacity={0.78}
                    style={{ cursor: 'pointer', filter: 'drop-shadow(0 1px 4px #b0b6c3)' }}
                    onMouseEnter={e => {
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        gene: gene,
                        expr: df.data[gene],
                        visible: true
                      });
                    }}
                    onMouseMove={e => {
                      setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }));
                    }}
                    onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
                  />
                  {showGeneLabels && (
                    <text
                      x={scaleXZoom(x)}
                      y={scaleYZoom(y) - 12}
                      fontSize={13}
                      textAnchor="middle"
                      fill="#2a2a4a"
                      style={{ pointerEvents: 'none', fontWeight: 600, userSelect: 'none', textShadow: '0 1px 4px #fff' }}
                    >
                      {gene}
                    </text>
                  )}
                </g>
              );
            }
            return null;
          })}
          {/* Highlighted gene on top */}
          {highlightedGene && coords.map(([x, y], i) => {
            const gene = umapGenes[i];
            if (gene === highlightedGene) {
              return (
                <g key={gene}>
                  <circle
                    cx={scaleXZoom(x)}
                    cy={scaleYZoom(y)}
                    r={13}
                    fill="#e94f37"
                    stroke="#fff"
                    strokeWidth={3}
                    opacity={1}
                    style={{ cursor: 'pointer', filter: 'drop-shadow(0 2px 10px #e94f37)' }}
                    onMouseEnter={e => {
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        gene: gene,
                        expr: df.data[gene],
                        visible: true
                      });
                    }}
                    onMouseMove={e => {
                      setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }));
                    }}
                    onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
                  />
                  {showGeneLabels && (
                    <text
                      x={scaleXZoom(x)}
                      y={scaleYZoom(y) - 18}
                      fontSize={16}
                      textAnchor="middle"
                      fill="#e94f37"
                      style={{ pointerEvents: 'none', fontWeight: 800, userSelect: 'none', textShadow: '0 2px 8px #fff' }}
                    >
                      {gene}
                    </text>
                  )}
                </g>
              );
            }
            return null;
          })}
        </svg>
      </div>
      {tooltip.visible && tooltip.expr && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 18,
            top: tooltip.y + 12,
            background: 'rgba(30,30,30,0.97)',
            color: '#fff',
            padding: '12px 18px',
            borderRadius: 8,
            fontSize: 15,
            pointerEvents: 'none',
            zIndex: 100,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            fontWeight: 500,
            minWidth: 180,
            maxWidth: 340,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{tooltip.gene}</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14, background: 'none', color: '#fff' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '2px 8px 2px 0', fontWeight: 600, color: '#b0b6c3', background: 'none' }}>Sample</th>
                <th style={{ textAlign: 'right', padding: '2px 0 2px 8px', fontWeight: 600, color: '#b0b6c3', background: 'none' }}>Expr</th>
              </tr>
            </thead>
            <tbody>
              {samples.map((s: string) => (
                <tr key={s}>
                  <td style={{ textAlign: 'left', padding: '2px 8px 2px 0', background: 'none' }}>{s}</td>
                  <td style={{ textAlign: 'right', padding: '2px 0 2px 8px', background: 'none' }}>{tooltip.expr && tooltip.expr[s] != null ? tooltip.expr[s].toFixed(3) : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 16, color: '#444', marginTop: 18, textAlign: 'center', lineHeight: 1.6, background: '#f7f8fa', borderRadius: 9, padding: 14, maxWidth: 900, marginLeft: 'auto', marginRight: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', fontWeight: 500 }}>
        <b>Note:</b> Each point is a gene projected onto a 2D UMAP embedding of the expression matrix. Genes with similar expression profiles are close together.<br />
        <span style={{ color: '#2a2a4a' }}>
          Point color intensity reflects the number of sample groups in which each gene appears in the top 50 (i.e., the top 50 rank hits overlap count).
        </span>
      </div>
    </div>
  );
};

export default UmapChart;
